/**
 * The controls that sit over the KPI grid.
 *
 * Everything here changes what the GRID shows. The two controls that act on the
 * dashboard as a whole — Download and the Analyst toggle — moved into the shell
 * header and tab strip with DOP-479, so this row no longer mixes the two
 * altitudes. The property chips and the period switch sit on the row above,
 * shared with the Charts tab; this row is the Table's own (DOP-609).
 *
 * It wraps rather than squeezes: beside the Analyst rail the row is narrower
 * than its controls.
 */

import { CircleHelp, Plus, RefreshCw, RotateCcw } from "lucide-react";
import React from "react";

import KpiEmptyRowsToggle from "./KpiEmptyRowsToggle";

export interface KpiDataToolbarProps {
	refreshing: boolean;
	onRefresh: () => void;
	hideEmptyRows: boolean;
	onHideEmptyRowsChange: (hide: boolean) => void;
	/** Rows with no number in any column — the ones the switch acts on. */
	emptyRowCount: number;
	onAddMetric: () => void;
	onStartTour: () => void;
	/** This user has moved rows (DOP-613); Reset order has something to undo. */
	orderCustomized?: boolean;
	onResetOrder?: () => void;
	/** The last move was not saved to the server. */
	orderSaveFailed?: boolean;
}

const BUTTON =
	"inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-lg border bg-white px-3 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-[#008782] disabled:opacity-50";

const KpiDataToolbar: React.FC<KpiDataToolbarProps> = ({
	refreshing,
	onRefresh,
	hideEmptyRows,
	onHideEmptyRowsChange,
	emptyRowCount,
	onAddMetric,
	onStartTour,
	orderCustomized = false,
	onResetOrder,
	orderSaveFailed = false,
}) => (
	<div className="flex shrink-0 flex-wrap items-center gap-3">
		<KpiEmptyRowsToggle
			hideEmptyRows={hideEmptyRows}
			onChange={onHideEmptyRowsChange}
		/>
		<span className="text-xs text-[#8a9099]">
			{emptyRowCount === 0
				? "Every row has numbers"
				: hideEmptyRows
					? `${emptyRowCount} empty ${emptyRowCount === 1 ? "row" : "rows"} hidden`
					: `${emptyRowCount} ${emptyRowCount === 1 ? "row has" : "rows have"} no numbers`}
		</span>
		<div className="ml-auto flex flex-wrap items-center gap-3">
			{orderSaveFailed && (
				<span role="status" className="text-xs text-red-600">
					Your row order could not be saved.
				</span>
			)}
			{orderCustomized && onResetOrder && (
				<button
					type="button"
					onClick={onResetOrder}
					title="Put the rows back in the default order"
					className={`${BUTTON} border-gray-200 text-text-strong hover:bg-gray-50`}
				>
					<RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
					Reset order
				</button>
			)}
			<button
				type="button"
				onClick={onAddMetric}
				className={`${BUTTON} border-gray-200 text-text-strong hover:bg-gray-50`}
			>
				<Plus className="h-3.5 w-3.5" aria-hidden="true" />
				Add calculated row
			</button>
			<button
				type="button"
				onClick={onRefresh}
				disabled={refreshing}
				className={`${BUTTON} border-gray-200 text-text-strong hover:bg-gray-50`}
			>
				<RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
				{refreshing ? "Refreshing…" : "Refresh"}
			</button>
			<button
				type="button"
				onClick={onStartTour}
				className={`${BUTTON} border-[#99CFCD] text-[#004C4B] hover:bg-[#f6fbfb]`}
			>
				<CircleHelp className="h-3.5 w-3.5" aria-hidden="true" />
				Take the tour
			</button>
		</div>
	</div>
);

export default KpiDataToolbar;
